// =====================================================================
// iSentinel360 v0.4.1 — Offline string bundle
//
// === Why this exists ===
//
// The PWA's normal text uses `i18next` and the `useTranslation` hook.
// Translation files live at `/locales/{lang}/common.json` and similar,
// loaded over the network on app start and on language change.
//
// If the user is OFFLINE when they open the PWA (or lose connectivity
// while using it), the translation files can't be downloaded. In that
// case `t('offline.title')` returns the KEY STRING itself (e.g.
// "offline.title") — which is what the user was seeing before this
// fix: a banner with the literal text "offline.title" in it.
//
// This file bypasses i18next entirely for the two strings the offline
// banner needs. They are bundled directly into the PWA's JavaScript,
// so they're available the instant the bundle loads — no network
// round trip, no i18next dependency, no translation file fetch.
//
// === Cost ===
//
// 14 languages × 2 strings ≈ 1.5 KB minified. Trivial.
//
// === Adding more languages or strings ===
//
// Just add a key to both `NO_CONNECTION` and `NO_CONNECTION_DESC`.
// The lookup falls back to English if the current language is not
// in the table, so missing entries are safe.
// =====================================================================

const NO_CONNECTION: Record<string, string> = {
  en: "No Internet Connection",
  es: "Sin Conexión a Internet",
  fr: "Aucune Connexion Internet",
  de: "Keine Internetverbindung",
  pt: "Sem Conexão Com a Internet",
  it: "Nessuna Connessione Internet",
  nl: "Geen Internetverbinding",
  pl: "Brak Połączenia z Internetem",
  ru: "Нет подключения к интернету",
  tr: "İnternet Bağlantısı Yok",
  ar: "لا يوجد اتصال بالإنترنت",
  zh: "没有网络连接",
  ja: "インターネットに接続されていません",
  ko: "인터넷 연결 없음",
};

const NO_CONNECTION_DESC: Record<string, string> = {
  en: "No internet connection - some features won't work until your connection is back",
  es: "Sin conexión a internet: algunas funciones no funcionarán hasta que se restablezca la conexión",
  fr: "Aucune connexion internet - certaines fonctionnalités ne fonctionneront pas jusqu'au rétablissement de la connexion",
  de: "Keine Internetverbindung - einige Funktionen werden nicht funktionieren, bis die Verbindung wiederhergestellt ist",
  pt: "Sem conexão com a internet - alguns recursos não funcionarão até que a conexão seja restaurada",
  it: "Nessuna connessione internet: alcune funzioni non funzioneranno fino al ripristino della connessione",
  nl: "Geen internetverbinding - sommige functies zullen niet werken totdat de verbinding is hersteld",
  pl: "Brak połączenia z internetem - niektóre funkcje nie będą działać do czasu przywrócenia połączenia",
  ru: "Нет подключения к интернету — некоторые функции не будут работать до восстановления связи",
  tr: "İnternet bağlantısı yok - bağlantınız geri gelene kadar bazı özellikler çalışmayacak",
  ar: "لا يوجد اتصال بالإنترنت - لن تعمل بعض الميزات حتى يعود الاتصال",
  zh: "没有网络连接 - 在连接恢复之前，某些功能将无法使用",
  ja: "インターネットに接続されていません。接続が復旧するまで、一部の機能は利用できません",
  ko: "인터넷 연결 없음 - 연결이 복구될 때까지 일부 기능은 작동하지 않습니다",
};

/**
 * Look up the offline "No connection" title in the user's current
 * language. Falls back to:
 *   1. exact language code match (e.g. "es")
 *   2. language root match (e.g. "es-MX" → "es")
 *   3. English
 *
 * @param language The i18next language code, e.g. from
 *                 `useTranslation().i18n.language`. Pass an empty
 *                 string or undefined and you'll get the English
 *                 fallback (safe default).
 */
export function getNoConnectionText(language?: string): string {
  if (!language) return NO_CONNECTION.en;
  if (NO_CONNECTION[language]) return NO_CONNECTION[language];
  const root = language.split('-')[0];
  if (NO_CONNECTION[root]) return NO_CONNECTION[root];
  return NO_CONNECTION.en;
}

/**
 * Look up the offline "No connection" description in the user's
 * current language. Same fallback chain as `getNoConnectionText`.
 */
export function getNoConnectionDescText(language?: string): string {
  if (!language) return NO_CONNECTION_DESC.en;
  if (NO_CONNECTION_DESC[language]) return NO_CONNECTION_DESC[language];
  const root = language.split('-')[0];
  if (NO_CONNECTION_DESC[root]) return NO_CONNECTION_DESC[root];
  return NO_CONNECTION_DESC.en;
}
